import React, { useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

// Backend'den gelen periyot anahtarını (YYYY-MM, YYYY-44, vb.) x ekseni etiketine çevirir
function formatPeriodDisplay(dateKey, groupBy) {
  if (!dateKey) return '';
  const str = String(dateKey).trim().replace(/\s/g, '');
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  if (groupBy === 'month' && /^\d{4}-\d{2}$/.test(str)) {
    const [y, m] = str.split('-').map(Number);
    return `${months[m - 1]} ${y}`;
  }
  if (groupBy === 'week') {
    const match = str.match(/^(\d{4})[-]?(\d{1,2})$/);
    if (match) return `${match[1]} - Hafta ${parseInt(match[2], 10)}`;
    const parsed = new Date(dateKey);
    if (!isNaN(parsed.getTime())) return `${parsed.getFullYear()} - Hafta ${Math.ceil(parsed.getDate() / 7)}`;
    return str;
  }
  if (groupBy === 'quarter' && /^\d{4}-Q?\d$/i.test(str)) {
    const [y, q] = str.replace(/Q/i, '-').split('-').filter(Boolean).map((x, i) => i === 1 ? parseInt(x, 10) : x);
    return `Q${q} ${y}`;
  }
  if (groupBy === 'year' && /^\d{4}$/.test(str)) return str;
  const parsed = new Date(dateKey);
  if (!isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return str;
}

// Grafik tipini belirleme helper'ı
// breakdownWithDates: by_gender, by_age_group, by_file zaman serisi (kümülatif) ise line kullan
const getChartType = (metric, breakdownWithDates = false) => {
  const cumulativeBreakdownMetrics = ['by_gender', 'by_age_group', 'by_file'];
  if (breakdownWithDates && cumulativeBreakdownMetrics.includes(metric)) {
    return 'line'; // kümülatif zaman serisi: çizgi grafik
  }
  // Diğer breakdown metrikleri bar
  if (metric?.includes('by_') || metric === 'by_gender' || metric === 'by_age_group' ||
      metric === 'by_type' || metric === 'by_file' || metric === 'by_form' || metric === 'with_formula') {
    return 'bar';
  }
  return 'line';
};

/** Doktor paneli grafikleri (PatientsStatistics vb.) ile uyumlu — tema `tokens` paleti */
function buildSeriesPalette(c) {
  return [
    c.blueAccent[500],
    c.greenAccent[500],
    c.redAccent[500],
    c.blueAccent[400],
    c.greenAccent[400],
    c.blueAccent[600],
    c.greenAccent[600],
    c.redAccent[400],
    c.blueAccent[300],
    c.greenAccent[300],
    c.blueAccent[700],
    c.greenAccent[700],
    c.redAccent[300],
    c.blueAccent[200],
    c.greenAccent[200],
  ];
}

// Aynı değerde ilerleyen çizgileri ayırt etmek için her seriye farklı çizgi deseni (solid, dashed, dotted)
const ENTITY_STROKE_DASHARRAY = [
  'none',      // 0: düz
  '5 5',       // 1: kesikli
  '2 3',       // 2: noktalı
  '8 4',       // 3: uzun çizgi
  '3 4 2 4',   // 4: nokta-çizgi
  '1 3',       // 5: ince noktalı
  '6 3 2 3',   // 6: çift kesik
  '4 4',       // 7: orta kesik
  '10 5',      // 8: uzun kesik
];

export default function StatsChart({ data, loading, error, metric, entity, entities, entityLabels, files = [], period, groupBy, preferredChartType }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const seriesPalette = useMemo(() => buildSeriesPalette(colors), [colors]);
  const { t } = useTranslation('stats');
  const isMultiEntity = entities && Array.isArray(entities) && entities.length > 0;
  const singleData = useMemo(() => {
    if (!data) return null;
    if (entity && typeof data === 'object' && !Array.isArray(data) && data.series === undefined && data[entity]) return data[entity];
    return data;
  }, [data, entity]);

  const hasBreakdownWithDates = useMemo(() => {
    if (!singleData?.series || !Array.isArray(singleData.series)) return false;
    const hasGroup = singleData.series.some(item => item.group);
    const hasDate = singleData.series.some(item => item.date != null);
    return hasGroup && hasDate;
  }, [singleData?.series]);

  const chartType = useMemo(
    () => (preferredChartType === 'line' || preferredChartType === 'bar' ? preferredChartType : getChartType(metric, hasBreakdownWithDates)),
    [metric, hasBreakdownWithDates, preferredChartType]
  );
  
  // File ID'leri file isimlerine çevirme helper'ı
  const getFileLabel = useMemo(() => {
    // File map oluştur (ID -> Name)
    const fileMap = new Map();
    files.forEach(file => {
      if (file.id && file.name) {
        fileMap.set(String(file.id), file.name);
      }
    });
    
    return (label) => {
      // Eğer label bir sayıysa (file ID olabilir), file ismini döndür
      if (fileMap.has(String(label))) {
        return fileMap.get(String(label));
      }
      // Değilse orijinal label'ı döndür
      return label;
    };
  }, [files]);
  
  // Eksik tarihleri doldurma helper'ı (sadece day group_by için)
  const fillMissingDates = useMemo(() => {
    if (!period || !groupBy || groupBy !== 'day' || !singleData?.period) return null;
    
    const { start_date, end_date } = singleData.period;
    if (!start_date || !end_date) return null;
    
    // YYYY-MM-DD formatındaki tarihleri parse et (local time kullanarak)
    const parseLocalDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    
    // Tarih aralığındaki tüm günleri oluştur
    const start = parseLocalDate(start_date);
    const end = parseLocalDate(end_date);
    const dates = [];
    
    const current = new Date(start);
    while (current <= end) {
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      dates.push(dateStr);
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  }, [period, groupBy, data?.period]);

  // Çoklu entity: tüm serileri tarihe göre birleştir (her entity farklı renkte)
  const multiEntityChartData = useMemo(() => {
    if (!isMultiEntity || !data || !entities.length) return [];
    const dateMap = new Map();
    entities.forEach((ent) => {
      const block = data[ent];
      if (!block?.series || !Array.isArray(block.series)) return;
      block.series.forEach((item) => {
        let dateKey = item.date ?? item.label;
        if (dateKey == null) return;
        if (dateKey instanceof Date) {
          dateKey = `${dateKey.getFullYear()}-${String(dateKey.getMonth() + 1).padStart(2, '0')}-${String(dateKey.getDate()).padStart(2, '0')}`;
        } else if (typeof dateKey === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          const parsed = new Date(dateKey);
          if (!isNaN(parsed.getTime())) {
            dateKey = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
          }
        }
        if (!dateMap.has(dateKey)) {
          const row = { date: dateKey, name: formatPeriodDisplay(dateKey, groupBy) };
          entities.forEach((e) => { row[e] = 0; });
          dateMap.set(dateKey, row);
        }
        dateMap.get(dateKey)[ent] = item.value ?? 0;
      });
    });
    return Array.from(dateMap.values()).sort((a, b) => {
      const dA = new Date(a.date);
      const dB = new Date(b.date);
      if (!isNaN(dA.getTime()) && !isNaN(dB.getTime())) return dA - dB;
      return String(a.date).localeCompare(String(b.date));
    });
  }, [isMultiEntity, data, entities, groupBy]);

  // Breakdown metrikleri için (by_type, by_file, vb.) seri listesi oluştur
  // Hem orijinal label'ları (file ID'leri) hem de display label'ları (file isimleri) sakla
  const breakdownSeries = useMemo(() => {
    if (!singleData?.series || !Array.isArray(singleData.series)) return [];
    
    // group alanına göre breakdown metrikleri tespit et
    const hasGroup = singleData.series.some(item => item.group);
    if (!hasGroup) return [];
    
    // Tüm unique label'ları (tip/file isimleri) bul - orijinal label'ları sakla
    const uniqueLabels = [...new Set(singleData.series.map(item => item.label).filter(Boolean))];
    return uniqueLabels;
  }, [singleData?.series]);
  
  // File ID'den file ismine mapping (by_file için)
  // Hem ID'den isme, hem de isimden isme (eğer zaten isimse) mapping yap
  const fileLabelMap = useMemo(() => {
    if (metric !== 'by_file' || !files.length) return new Map();
    const map = new Map();
    files.forEach(file => {
      if (file.id && file.name) {
        // ID'den isme mapping
        map.set(String(file.id), file.name);
        // Eğer backend'den gelen label zaten bir isimse, kendisiyle mapping yap
        map.set(file.name, file.name);
      }
    });
    return map;
  }, [metric, files]);

  // Recharts için veri formatı (tek entity; çoklu entity multiEntityChartData kullanır)
  const chartData = useMemo(() => {
    if (isMultiEntity) return multiEntityChartData;
    if (!singleData?.series || !Array.isArray(singleData.series)) return [];
    
    // Files - Forms by File: dosya adına göre form sayısı (bar chart, x = dosya adı, y = form sayısı)
    if (entity === 'files' && metric === 'forms_by_file' && singleData.series.length > 0) {
      return singleData.series.map((s) => ({
        name: getFileLabel(s.label) || s.label || 'N/A',
        value: s.value ?? 0
      }));
    }
    
    // Breakdown metrikleri için özel format (by_type, by_file, vb.)
    if (breakdownSeries.length > 0 && singleData.series.some(item => item.group)) {
      // Tüm seri date: null ise (group_by yok): basit bar formatı [ { name: dosya adı, value } ]
      const allNullDate = singleData.series.every(item => item.date == null);
      if (allNullDate) {
        return singleData.series.map((s) => {
          let displayName = s.label;
          if (metric === 'by_file' && fileLabelMap.size > 0) {
            const labelStr = String(s.label);
            displayName = fileLabelMap.has(labelStr)
              ? fileLabelMap.get(labelStr)
              : (files.find(f => f.name === labelStr)?.name || s.label);
          }
          return { name: displayName, value: s.value ?? 0 };
        });
      }

      // Tarih bazında gruplama yap
      const dateMap = new Map();
      
      singleData.series.forEach(item => {
        // Date normalize et (sıralama için tutarlı key; week "2026-44" olduğu gibi kalır)
        let dateKey = item.date;
        if (!dateKey || dateKey === null) {
          return;
        }
        
        if (dateKey instanceof Date) {
          const year = dateKey.getFullYear();
          const month = String(dateKey.getMonth() + 1).padStart(2, '0');
          const day = String(dateKey.getDate()).padStart(2, '0');
          dateKey = `${year}-${month}-${day}`;
        } else if (typeof dateKey === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(String(dateKey).trim())) {
          dateKey = String(dateKey).trim();
        } else if (typeof dateKey === 'string' && /^\d{4}-\d{2}$/.test(String(dateKey).trim())) {
          dateKey = String(dateKey).trim();
        } else if (typeof dateKey === 'string') {
          dateKey = String(dateKey).trim().replace(/\s/g, '');
        }
        
        if (!dateMap.has(dateKey)) {
          const formattedDate = formatPeriodDisplay(dateKey, groupBy);
          
          // breakdownSeries'deki label'ları (file ID'leri) display label'lara (file isimleri) çevir
          const initialData = {};
          breakdownSeries.forEach(originalLabel => {
            let displayLabel = originalLabel;
            if (metric === 'by_file' && fileLabelMap.size > 0) {
              const labelStr = String(originalLabel);
              if (fileLabelMap.has(labelStr)) {
                displayLabel = fileLabelMap.get(labelStr);
              } else {
                // Eğer map'te yoksa, label zaten bir isim olabilir
                const matchingFile = files.find(f => f.name === labelStr);
                displayLabel = matchingFile ? matchingFile.name : originalLabel;
              }
            }
            initialData[displayLabel] = 0;
          });
          
          dateMap.set(dateKey, {
            date: dateKey,
            name: formattedDate,
            ...initialData
          });
        }
        
        let label = item.label;
        let displayLabel = label;
        
        // Eğer by_file metrik ise ve files varsa, file ID'yi file ismine çevir
        if (metric === 'by_file' && fileLabelMap.size > 0) {
          // Önce string olarak kontrol et
          const labelStr = String(label);
          if (fileLabelMap.has(labelStr)) {
            displayLabel = fileLabelMap.get(labelStr);
          } else {
            // Eğer map'te yoksa, label zaten bir isim olabilir
            // Files listesinde bu isimle eşleşen bir file var mı kontrol et
            const matchingFile = files.find(f => f.name === labelStr);
            if (matchingFile) {
              displayLabel = matchingFile.name;
            } else {
              // Hiçbir eşleşme yoksa, orijinal label'ı kullan
              displayLabel = label;
            }
          }
        }
        
        // dateMap'te displayLabel kullan (file ismi)
        if (displayLabel && dateMap.has(dateKey)) {
          dateMap.get(dateKey)[displayLabel] = item.value || 0;
        }
      });
      
      const isCumulativeBreakdown = ['by_gender', 'by_age_group', 'by_file'].includes(metric);
      const getDisplayLabel = (originalLabel) => {
        if (metric === 'by_file' && fileLabelMap.size > 0) {
          const labelStr = String(originalLabel);
          if (fileLabelMap.has(labelStr)) return fileLabelMap.get(labelStr);
          const matchingFile = files.find(f => f.name === labelStr);
          return matchingFile ? matchingFile.name : originalLabel;
        }
        return originalLabel;
      };

      // Kümülatif metriklerde eksik tarihleri "carry-forward" (önceki değer taşınır); diğerlerinde 0
      if (groupBy === 'day' && fillMissingDates) {
        if (isCumulativeBreakdown) {
          const sortedDates = [...fillMissingDates].sort((a, b) =>
            String(a).localeCompare(String(b), undefined, { numeric: true })
          );
          const lastValue = {};
          const result = sortedDates.map((dateStr) => {
            let row;
            if (dateMap.has(dateStr)) {
              row = { ...dateMap.get(dateStr) };
            } else {
              const dateObj = new Date(dateStr);
              const formattedDate = dateObj.toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              });
              row = { date: dateStr, name: formattedDate };
              breakdownSeries.forEach((originalLabel) => {
                const displayLabel = getDisplayLabel(originalLabel);
                row[displayLabel] = lastValue[displayLabel] ?? 0;
              });
            }
            breakdownSeries.forEach((originalLabel) => {
              const displayLabel = getDisplayLabel(originalLabel);
              const v = row[displayLabel];
              if (v !== undefined && v !== null) lastValue[displayLabel] = Number(v);
            });
            return row;
          });
          return result;
        }
        fillMissingDates.forEach((dateStr) => {
          if (!dateMap.has(dateStr)) {
            const dateObj = new Date(dateStr);
            const formattedDate = dateObj.toLocaleDateString('tr-TR', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            });
            const initialData = {};
            breakdownSeries.forEach((originalLabel) => {
              initialData[getDisplayLabel(originalLabel)] = 0;
            });
            dateMap.set(dateStr, { date: dateStr, name: formattedDate, ...initialData });
          }
        });
      }

      // Tarihe göre sırala (week/month key'leri new Date ile geçersiz olabildiği için string ile)
      return Array.from(dateMap.values()).sort((a, b) => {
        const dA = new Date(a.date);
        const dB = new Date(b.date);
        if (!isNaN(dA.getTime()) && !isNaN(dB.getTime())) return dA - dB;
        return String(a.date).localeCompare(String(b.date));
      });
    }
    
    // Normal metrikler için (count, new_count, vb.)
    const dataMap = new Map();
    singleData.series.forEach(item => {
      // Backend'den gelen date formatını normalize et (YYYY-MM-DD)
      let dateKey = item.date || item.label;
      if (dateKey && dateKey !== null) {
        // Eğer date bir Date objesi ise, string'e çevir
        if (dateKey instanceof Date) {
          const year = dateKey.getFullYear();
          const month = String(dateKey.getMonth() + 1).padStart(2, '0');
          const day = String(dateKey.getDate()).padStart(2, '0');
          dateKey = `${year}-${month}-${day}`;
        }
        // Eğer date string ise ve format YYYY-MM-DD değilse, parse et
        else if (typeof dateKey === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
          const parsed = new Date(dateKey);
          if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            dateKey = `${year}-${month}-${day}`;
          }
        }
        
        let label = item.label || item.date || 'N/A';
        
        // Eğer by_file metrik ise ve files varsa, file ID'yi file ismine çevir
        if (metric === 'by_file' && files.length > 0) {
          label = getFileLabel(label);
        }
        
        dataMap.set(dateKey, {
          name: label,
          value: item.value || 0,
          date: dateKey,
          originalLabel: item.label
        });
      }
    });
    
    // Eğer day group_by ise ve fillMissingDates varsa, eksik tarihleri doldur
    if (groupBy === 'day' && fillMissingDates) {
      const result = [];
      fillMissingDates.forEach(dateStr => {
        if (dataMap.has(dateStr)) {
          result.push(dataMap.get(dateStr));
        } else {
          // Eksik tarih için 0 değeri ekle
          const dateObj = new Date(dateStr);
          const formattedDate = dateObj.toLocaleDateString('tr-TR', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
          });
          result.push({
            name: formattedDate,
            value: 0,
            date: dateStr,
            originalLabel: dateStr
          });
        }
      });
      return result;
    }
    
    // Diğer durumlarda mevcut verileri döndür
    return Array.from(dataMap.values());
  }, [singleData, isMultiEntity, multiEntityChartData, entity, metric, files, getFileLabel, groupBy, fillMissingDates, breakdownSeries, fileLabelMap]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 400,
        }}
      >
        <CircularProgress sx={{ color: colors.greenAccent[500] }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 400,
          color: colors.redAccent[500]
        }}
      >
        <Typography>Error loading chart data</Typography>
      </Box>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 400,
          color: colors.grey[400]
        }}
      >
        <Typography>No data available</Typography>
      </Box>
    );
  }

  // Tooltip custom style
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Eğer by_file metrik ise ve files varsa, label'ı file ismine çevir
      let displayLabel = label;
      if (metric === 'by_file' && files.length > 0) {
        displayLabel = getFileLabel(label);
      }
      
      return (
        <Box
          sx={{
            backgroundColor: colors.primary[800],
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 1,
            p: 1.5
          }}
        >
          <Typography sx={{ color: colors.grey[100], mb: 0.5, fontWeight: 600 }}>
            {displayLabel}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              sx={{ color: entry.color, fontSize: '0.875rem' }}
            >
              {`${entry.name || 'Value'}: ${entry.value}`}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Line Chart
  if (chartType === 'line') {
    // Breakdown metrikleri için (by_type, by_file, vb.) her label için ayrı Line
    if (breakdownSeries.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
            <XAxis
              dataKey="name"
              stroke={colors.grey[300]}
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis
              stroke={colors.grey[300]}
              style={{ fontSize: '0.75rem' }}
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <Box
                      sx={{
                        backgroundColor: colors.primary[800],
                        border: `1px solid ${colors.grey[700]}`,
                        borderRadius: 1,
                        p: 1.5
                      }}
                    >
                      <Typography sx={{ color: colors.grey[100], mb: 0.5, fontWeight: 600 }}>
                        {label}
                      </Typography>
                      {payload.map((entry, index) => (
                        <Typography
                          key={index}
                          sx={{ color: entry.color, fontSize: '0.875rem' }}
                        >
                          {`${entry.name}: ${entry.value}`}
                        </Typography>
                      ))}
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={{ color: colors.grey[300] }}
            />
            {breakdownSeries.map((originalLabel, index) => {
              // Eğer by_file metrik ise ve files varsa, file ID'yi file ismine çevir
              let displayLabel = originalLabel;
              if (metric === 'by_file' && fileLabelMap.size > 0) {
                displayLabel = fileLabelMap.has(String(originalLabel)) 
                  ? fileLabelMap.get(String(originalLabel)) 
                  : originalLabel;
              }
              
              return (
                <Line
                  key={originalLabel}
                  type="monotone"
                  dataKey={displayLabel}
                  stroke={seriesPalette[index % seriesPalette.length]}
                  strokeWidth={2}
                  dot={{ fill: seriesPalette[index % seriesPalette.length], r: 4 }}
                  activeDot={{ r: 6 }}
                  name={displayLabel}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      );
    }
    
    // Çoklu entity: her entity için ayrı renkte Line
    if (isMultiEntity && entities?.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
            <XAxis
              dataKey="name"
              stroke={colors.grey[300]}
              style={{ fontSize: '0.75rem' }}
            />
            <YAxis
              stroke={colors.grey[300]}
              style={{ fontSize: '0.75rem' }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <Box
                      sx={{
                        backgroundColor: colors.primary[800],
                        border: `1px solid ${colors.grey[700]}`,
                        borderRadius: 1,
                        p: 1.5
                      }}
                    >
                      <Typography sx={{ color: colors.grey[100], mb: 0.5, fontWeight: 600 }}>
                        {label}
                      </Typography>
                      {payload.map((entry, index) => (
                        <Typography
                          key={index}
                          sx={{ color: entry.color, fontSize: '0.875rem' }}
                        >
                          {`${entry.name}: ${entry.value}`}
                        </Typography>
                      ))}
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ color: colors.grey[300] }} />
            {entities.map((ent, index) => {
              const color = seriesPalette[index % seriesPalette.length];
              const dash = ENTITY_STROKE_DASHARRAY[index % ENTITY_STROKE_DASHARRAY.length];
              return (
                <Line
                  key={ent}
                  type="monotone"
                  dataKey={ent}
                  stroke={color}
                  strokeWidth={2.5}
                  strokeDasharray={dash === 'none' ? undefined : dash}
                  dot={{ fill: color, r: 5, strokeWidth: 2, stroke: colors.primary[800] }}
                  activeDot={{ r: 7, strokeWidth: 2, stroke: colors.grey[100] }}
                  name={entityLabels?.[ent] ?? t(`entities.${ent}`) ?? ent}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // Normal metrikler için tek Line
    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="name"
            stroke={colors.grey[300]}
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis
            stroke={colors.grey[300]}
            style={{ fontSize: '0.75rem' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ color: colors.grey[300] }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={colors.blueAccent[500]}
            strokeWidth={2}
            dot={{ fill: colors.blueAccent[500], r: 4 }}
            activeDot={{ r: 6 }}
            name="Value"
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Area Chart (alternatif olarak)
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="name"
            stroke={colors.grey[300]}
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis
            stroke={colors.grey[300]}
            style={{ fontSize: '0.75rem' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ color: colors.grey[300] }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={colors.greenAccent[500]}
            fill={colors.greenAccent[500]}
            fillOpacity={0.3}
            name="Value"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // Bar Chart
  if (chartType === 'bar') {
    // Breakdown metrikleri için (by_type, by_file, vb.) her label için ayrı Bar
    if (breakdownSeries.length > 0) {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
            <XAxis
              dataKey="name"
              stroke={colors.grey[300]}
              style={{ fontSize: '0.75rem' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke={colors.grey[300]}
              style={{ fontSize: '0.75rem' }}
            />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <Box
                      sx={{
                        backgroundColor: colors.primary[800],
                        border: `1px solid ${colors.grey[700]}`,
                        borderRadius: 1,
                        p: 1.5
                      }}
                    >
                      <Typography sx={{ color: colors.grey[100], mb: 0.5, fontWeight: 600 }}>
                        {label}
                      </Typography>
                      {payload.map((entry, index) => (
                        <Typography
                          key={index}
                          sx={{ color: entry.color, fontSize: '0.875rem' }}
                        >
                          {`${entry.name}: ${entry.value}`}
                        </Typography>
                      ))}
                    </Box>
                  );
                }
                return null;
              }}
            />
            <Legend
              wrapperStyle={{ color: colors.grey[300] }}
            />
            {breakdownSeries.map((originalLabel, index) => {
              // Eğer by_file metrik ise ve files varsa, file ID'yi file ismine çevir
              let displayLabel = originalLabel;
              if (metric === 'by_file' && fileLabelMap.size > 0) {
                displayLabel = fileLabelMap.has(String(originalLabel)) 
                  ? fileLabelMap.get(String(originalLabel)) 
                  : originalLabel;
              }
              
              return (
                <Bar
                  key={originalLabel}
                  dataKey={displayLabel}
                  fill={seriesPalette[index % seriesPalette.length]}
                  name={displayLabel}
                  radius={[8, 8, 0, 0]}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      );
    }
    
    // Normal metrikler için tek Bar
    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
          <XAxis
            dataKey="name"
            stroke={colors.grey[300]}
            style={{ fontSize: '0.75rem' }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            stroke={colors.grey[300]}
            style={{ fontSize: '0.75rem' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ color: colors.grey[300] }}
          />
          <Bar
            dataKey="value"
            fill={colors.blueAccent[500]}
            name="Value"
            radius={[8, 8, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Pie Chart (opsiyonel - breakdown metrikleri için)
  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={120}
            fill={colors.blueAccent[500]}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={seriesPalette[index % seriesPalette.length]} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ color: colors.grey[300] }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return null;
}
